import { UPGRADE_FUNNEL_BROWSER_EVENT } from '@shared/core/analytics'
import type { UpgradeSourcePage } from '@shared/core/analytics'
import { REMINDERS_PAUSED_REASONS, resolveRemindersPausedReason } from '@shared/core/billing'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { FormattedText } from 'src/modules/core/components'
import { usePlanData } from 'src/modules/payment/hooks/use-plan-data'
import { useTrackUpgradeFunnel } from 'src/modules/payment/hooks/use-track-upgrade-funnel'
import { useUpdatePaymentMethod } from 'src/modules/payment/hooks/use-update-payment-method'
import { formatSubscriptionEndedOn } from 'src/modules/payment/utils/helpers'
import {
  buildUpgradePlansPath,
  isUpgradePlansView,
  resolveUpgradeSourcePage,
} from 'src/modules/payment/utils/upgrade-funnel-navigation'
import {
  canPostRunningLowShownEvent,
  dismissRunningLowBanner,
  isRunningLowBannerDismissed,
  markRunningLowShownEventPosted,
} from './paused-reminders-banner.domain'
import type {
  PausedBannerProps,
  PausedRemindersBannerProfile,
  PausedRemindersBannerProps,
} from './paused-reminders-banner.interface'
import classes from './paused-reminders-banner.module.scss'

export function PausedRemindersBanner({ profile }: PausedRemindersBannerProps) {
  const { pathname, search } = useLocation()
  const accountId = profile?.id
  const [isDismissed, setIsDismissed] = useState(() => isRunningLowBannerDismissed(accountId))
  const trackUpgradeFunnel = useTrackUpgradeFunnel()

  const searchParams = new URLSearchParams(search)
  const isPlansView = isUpgradePlansView(pathname, searchParams)
  const isPaused = Boolean(profile?.reminders_paused)
  const isRunningLow = !isPaused && Boolean(profile?.reminders_running_low) && !isDismissed
  const showsRunningLow = isRunningLow && !isPlansView

  useEffect(() => {
    setIsDismissed(isRunningLowBannerDismissed(accountId))
  }, [accountId])

  // The day slot is claimed on success only, so a failed post can be retried
  // on the next render of the banner instead of being lost for the whole day.
  useEffect(() => {
    if (!showsRunningLow) return
    if (!canPostRunningLowShownEvent(accountId)) return
    trackUpgradeFunnel(
      UPGRADE_FUNNEL_BROWSER_EVENT.QUOTA_WARNING_SHOWN,
      {},
      {
        onSuccess: () => markRunningLowShownEventPosted(accountId),
      },
    )
  }, [showsRunningLow, accountId, trackUpgradeFunnel])

  if (!profile || isPlansView || (!isPaused && !isRunningLow)) {
    return null
  }

  const sourcePage = resolveUpgradeSourcePage(pathname, searchParams)

  if (isPaused) {
    return <PausedBanner profile={profile} sourcePage={sourcePage} />
  }

  return (
    <RunningLowBanner
      profile={profile}
      sourcePage={sourcePage}
      onDismiss={() => {
        dismissRunningLowBanner(profile.id)
        setIsDismissed(true)
      }}
    />
  )
}

function PausedBanner({ profile, sourcePage }: PausedBannerProps) {
  const reason = resolveRemindersPausedReason(profile)
  if (reason === REMINDERS_PAUSED_REASONS.paymentPaused) {
    return <PaymentPausedBanner />
  }

  const plansPath = buildUpgradePlansPath({
    trigger: 'banner_click',
    source_element: 'banner',
    source_page: sourcePage,
  })

  return (
    <div className={classes.banner} data-testid="paused-reminders-banner">
      {reason === REMINDERS_PAUSED_REASONS.subscriptionEnded ? (
        <FormattedText
          id="payment.pausedBanner.message.subscriptionEnded"
          values={{ date: formatSubscriptionEndedOn(profile) }}
        />
      ) : (
        <FormattedText id="payment.pausedBanner.message" values={{ limit: profile.effective_free_reminders_limit }} />
      )}
      <Link to={plansPath} className={classes.link}>
        <FormattedText id="payment.pausedBanner.link" />
      </Link>
    </div>
  )
}

function PaymentPausedBanner() {
  const { currentPlan, loading } = usePlanData()
  const { openUpdateCard, isAvailable } = useUpdatePaymentMethod()

  // While the plans load, `currentPlan` falls back to the free plan's name.
  if (loading || !currentPlan) {
    return null
  }

  return (
    <div className={classes.banner} data-testid="paused-reminders-banner">
      <FormattedText id="payment.pausedBanner.message.paymentPaused" values={{ plan: currentPlan.name }} />
      <button
        type="button"
        className={classNames(classes.link, classes.linkButton)}
        onClick={openUpdateCard}
        disabled={!isAvailable}
      >
        <FormattedText id="payment.pausedBanner.link.paymentPaused" />
      </button>
    </div>
  )
}

type RunningLowBannerProps = {
  profile: PausedRemindersBannerProfile
  sourcePage: UpgradeSourcePage
  onDismiss: () => void
}

function RunningLowBanner({ profile, sourcePage, onDismiss }: RunningLowBannerProps) {
  const limit = profile.effective_free_reminders_limit
  const remaining = Math.max(0, limit - (profile.reminders_sent_during_billing_cycle ?? 0))
  const plansPath = buildUpgradePlansPath({
    trigger: 'pre_wall_warning',
    source_element: 'pre_wall_warning_banner',
    source_page: sourcePage,
  })

  return (
    <div className={classes.banner} data-testid="running-low-reminders-banner">
      <FormattedText id="payment.runningLowBanner.message" values={{ remaining, limit }} />
      <Link to={plansPath} className={classes.link}>
        <FormattedText id="payment.runningLowBanner.link" />
      </Link>
      <button type="button" className={classes.dismiss} onClick={onDismiss}>
        <FormattedText id="payment.runningLowBanner.dismiss" />
      </button>
    </div>
  )
}
